import React, { useRef } from "react";
import {
  FaBolt,
  FaPlug,
  FaMicrochip,
  FaChartLine,
  FaCheckCircle,
  FaIndustry,
  FaUserGraduate,
  FaAward,
  FaToolbox,
  FaWrench,
} from "react-icons/fa";

const ElectricianCourseSection = () => {
  const cardRefs = useRef([]);

  const handleMouseMove = (e, index) => {
    const card = cardRefs.current[index];
    if (!card) return;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / 20;
    const rotateY = (centerX - x) / 20;
    card.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale(1.02)`;
  };

  const handleMouseLeave = (index) => {
    const card = cardRefs.current[index];
    if (card) {
      card.style.transform = "perspective(1000px) rotateX(0) rotateY(0) scale(1)";
    }
  };

  const keywords = [
    "Power Generation",
    "Transmission Lines",
    "Substation",
    "AC/DC Circuits",
    "Lightning Arrestor",
    "Industrial Installation",
    "Switchgear",
    "Motor Control",
    "Earthing",
    "Solar Systems",
  ];

  const highlights = [
    "Hands‑on training in high‑voltage labs",
    "Industry‑expert faculty",
    "100% placement assistance in power sector",
    "Government‑recognized certification",
    "Live project work on industrial panels",
    "Soft skills & safety training",
  ];

  const skills = [
    { name: "Wiring & Cabling", level: 95 },
    { name: "Motor Control Circuits", level: 88 },
    { name: "Substation Maintenance", level: 85 },
    { name: "AC/DC Analysis", level: 90 },
  ];

  const tools = [
    { name: "Multimeter", icon: FaToolbox },
    { name: "Megger", icon: FaWrench },
    { name: "Crimping Tool", icon: FaToolbox },
    { name: "Clamp Meter", icon: FaBolt },
  ];

  const careerPaths = [
    { role: "Electrician", companies: "Power Plants, Industries", salary: "2.5 - 4 LPA" },
    { role: "Maintenance Electrician", companies: "Manufacturing Units", salary: "2.8 - 4.5 LPA" },
    { role: "Substation Operator", companies: "State Electricity Boards", salary: "3 - 5 LPA" },
    { role: "Solar Technician", companies: "Renewable Energy Firms", salary: "3.5 - 5.5 LPA" },
  ];

  return (
    <section className="w-full overflow-hidden bg-gradient-to-br from-orange-50 via-white to-green-50 py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 text-sm text-gray-500">
          <span className="text-orange-600">Home</span> / About RDS /{" "}
          <span className="text-green-600 font-semibold">ELECTRICIAN</span>
        </div>
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-8 items-start">
          <div className="space-y-6">
            <div className="inline-block rounded-full bg-orange-100 px-4 py-1 text-sm font-semibold text-orange-700">
              DURATION 2 YEARS | FULL TIME
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 md:text-4xl">
              ELECTRICIAN <span className="text-orange-500">Course</span>
            </h2>
            <p className="text-base leading-relaxed text-gray-700 md:text-lg">
              SKILLED ACQUIRED: On completion of the course, the trainee shall have thorough knowledge
              of generation, transmission, and distribution lines, overhead different types of insulators,
              earthing used in substations, power stations, lightning arrestors, reactors, and different
              types of capacitors in AC/DC circuits used in electronic and electrical installation in
              industries, etc.
            </p>
            <p className="text-base leading-relaxed text-gray-700 md:text-lg">
              The curriculum focuses on practical training in high‑voltage labs, industrial safety,
              and modern electrical systems. Graduates are equipped for roles in power generation,
              transmission, distribution, and industrial maintenance.
            </p>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Skills Covered</h3>
              <div className="flex flex-wrap gap-2">
                {keywords.map((kw, i) => (
                  <span
                    key={i}
                    className="rounded-full bg-white px-3 py-1 text-sm font-medium text-gray-700 shadow-sm transition-all hover:scale-105 hover:bg-orange-100 hover:text-orange-700 cursor-default"
                  >
                    {kw}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Why Choose This Course?</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                {highlights.map((item, i) => (
                  <div key={i} className="flex items-start gap-2 group transition hover:translate-x-1">
                    <FaCheckCircle className="mt-1 h-4 w-4 text-orange-500 group-hover:scale-110" />
                    <span className="text-sm text-gray-600">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-3">Core Competencies</h3>
              <div className="space-y-3">
                {skills.map((skill, i) => (
                  <div key={i}>
                    <div className="flex justify-between text-sm">
                      <span>{skill.name}</span>
                      <span>{skill.level}%</span>
                    </div>
                    <div className="h-2 w-full rounded-full bg-gray-200 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-orange-500 to-green-500 transition-all duration-1000"
                        style={{ width: `${skill.level}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="relative">
            <div className="sticky top-24 space-y-6">
              <div className="group relative overflow-hidden rounded-2xl shadow-xl">
                <img
                  src="image9.png"
                  alt="Electrician at work"
                  className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4 text-white translate-y-full group-hover:translate-y-0 transition-transform duration-300 bg-gradient-to-t from-black/80 to-transparent">
                  <p className="text-sm">Practical training in high‑voltage labs</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="rounded-xl bg-white p-4 text-center shadow-md transition-all hover:scale-105 hover:shadow-lg">
                  <FaUserGraduate className="mx-auto h-6 w-6 text-orange-500" />
                  <p className="text-2xl font-bold text-gray-800">400+</p>
                  <p className="text-xs text-gray-500">Students Trained</p>
                </div>
                <div className="rounded-xl bg-white p-4 text-center shadow-md transition-all hover:scale-105 hover:shadow-lg">
                  <FaAward className="mx-auto h-6 w-6 text-green-600" />
                  <p className="text-2xl font-bold text-gray-800">88%</p>
                  <p className="text-xs text-gray-500">Placement Rate</p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Tools & Equipment</h3>
                <div className="grid grid-cols-2 gap-3">
                  {tools.map((tool, idx) => (
                    <div
                      key={idx}
                      ref={(el) => (cardRefs.current[idx] = el)}
                      className="flex items-center gap-2 rounded-lg bg-white p-3 shadow transition-all duration-300 cursor-default"
                      onMouseMove={(e) => handleMouseMove(e, idx)}
                      onMouseLeave={() => handleMouseLeave(idx)}
                    >
                      <tool.icon className="h-5 w-5 text-orange-500" />
                      <span className="text-sm font-medium text-gray-700">{tool.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-20">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-gray-900 md:text-4xl">
              Career <span className="text-green-600">Opportunities</span>
            </h2>
            <p className="mt-2 text-gray-600">After completing the ELECTRICIAN course, you can explore these roles</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {careerPaths.map((job, idx) => (
              <div
                key={idx}
                className="group relative overflow-hidden rounded-2xl bg-white p-6 shadow-md transition-all hover:shadow-xl hover:-translate-y-1"
              >
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                <h3 className="text-xl font-bold text-gray-800">{job.role}</h3>
                <p className="mt-2 text-sm text-gray-600">{job.companies}</p>
                <p className="mt-2 text-sm font-semibold text-orange-600">{job.salary}</p>
              </div>
            ))}
          </div>
        </div>
        <div id="apply" className="mt-20 rounded-3xl bg-gradient-to-r from-orange-500 to-green-600 p-8 text-center text-white shadow-xl">
          <h3 className="text-2xl font-bold md:text-3xl">Start Your Electrical Career Today</h3>
          <p className="mt-2 text-lg opacity-90">Limited seats available – apply now for the upcoming batch.</p>
          <button className="mt-6 rounded-full bg-white px-8 py-3 font-semibold text-orange-600 shadow-md transition hover:scale-105 hover:shadow-lg">
            Apply for Admission
          </button>
        </div>
      </div>
      <style jsx>{`
        @keyframes fadeInLeft {
          from {
            opacity: 0;
            transform: translateX(-30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
        .animate-fadeInLeft {
          animation: fadeInLeft 0.8s ease-out forwards;
        }
        .animate-fadeInRight {
          animation: fadeInRight 0.8s ease-out forwards;
        }
      `}</style>
    </section>
  );
};

export default ElectricianCourseSection;